import { useQueries } from "@tanstack/react-query";

import type { ICategoryComparison } from "../../categories-context";
import type { IDemographicsApiAllData } from "../types";

import { fetchDemographicsData } from "./useDemographicsData";
import { usePageFilters } from "./usePageFilters";

type UseDemographicsComparisonDataParams<T> = {
    categories: ICategoryComparison[];
    select: (data: IDemographicsApiAllData, category: ICategoryComparison) => T;
};

const combineDemographicsComparisonData = <T>(
    results: Array<{ data: T | undefined; category: ICategoryComparison }>,
    originalCategoriesOrder: ICategoryComparison[],
): Record<string, T> | null => {
    const validResults = results.filter((result) => result.data !== undefined);

    if (validResults.length === 0) {
        return null;
    }

    const sortedResults = validResults.sort((a, b) => {
        const aIndex = originalCategoriesOrder.findIndex((cat) => cat.category.id === a.category.category.id);
        const bIndex = originalCategoriesOrder.findIndex((cat) => cat.category.id === b.category.category.id);
        return aIndex - bIndex;
    });

    return Object.fromEntries(sortedResults.map(({ data, category }) => [category.category.id, data])) as Record<
        string,
        T
    >;
};

export const useDemographicsComparisonData = <T>({ categories, select }: UseDemographicsComparisonDataParams<T>) => {
    const pageFilters = usePageFilters();

    return useQueries({
        queries: categories.map((category) => ({
            queryKey: ["demographics", pageFilters, category.category.forApi, category.category.id],
            queryFn: () => {
                // Use forUrl to ensure proper category parameter format
                const categoryParam = category.category.forUrl || category.category.categoryId || category.category.id;

                return fetchDemographicsData({
                    ...pageFilters,
                    category: categoryParam,
                });
            },
            select: (data: IDemographicsApiAllData) => select(data, category),
            enabled: !!(category.category.id && category.category.forApi),
        })),
        combine: (results) => {
            const resultsWithCategories = results.map((result, index) => ({
                data: result.data,
                category: categories[index],
            }));

            return {
                data: combineDemographicsComparisonData(resultsWithCategories, categories),
                isLoading: results.some((result) => result.isLoading),
                isError: results.some((result) => result.isError),
                error: results.find((result) => result.error)?.error,
            };
        },
    });
};
